import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  Skeleton,
  Slider,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { getStatusMap, updateShapeUpSettings } from '../../utils/shapeUpApi';

const SUMMIT = 0.5;

const zoneLabel = (position) => {
  if (position === null || position === undefined) return 'Off the hill';
  if (position < 0.08) return 'Not started';
  if (position < SUMMIT) return 'Figuring things out';
  if (position > 0.94) return 'Done';
  return 'Making it happen';
};

/**
 * Whether the hill actually fits this team's Jira workflow.
 *
 * The question a team asks when it first sees its board as a hill is "does this
 * mean anything?", and the honest answer depends entirely on whether its statuses
 * are ones the hill understands. This lists every status the boards use, where the
 * hill puts it, and which ones we are guessing at — because a guess held below the
 * peak is safe but silent, and mapping it is the two-minute fix.
 */
const HillStatusMap = ({ organizationId, canManage = false, onSaved }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [draft, setDraft] = useState({});
  // null until a status is parked or freed, then the edited set of parked keys.
  const [parkedDraft, setParkedDraft] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    if (!organizationId) return;
    setLoading(true);
    try {
      const result = await getStatusMap(organizationId);
      setData(result);
      setDraft({});
      setParkedDraft(null);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.message || 'Could not read the Jira statuses.');
    } finally {
      setLoading(false);
    }
  }, [organizationId]);

  useEffect(() => { load(); }, [load]);

  // Which statuses sit off the hill, from the edited draft or what the server
  // reported. Keys are already lower-cased by the server.
  const parkedSet = useMemo(() => {
    if (parkedDraft) return parkedDraft;
    return new Set(data?.parkedStatuses || []);
  }, [parkedDraft, data]);

  const statuses = useMemo(
    () => (data?.statuses || []).filter(status => !parkedSet.has(status.key)),
    [data, parkedSet]
  );
  const parkedStatuses = useMemo(
    () => (data?.statuses || []).filter(status => parkedSet.has(status.key)),
    [data, parkedSet]
  );

  const parkedDirty = parkedDraft !== null;
  const dirty = Object.keys(draft).length > 0 || parkedDirty;

  const setParked = (key, off) => {
    setParkedDraft(current => {
      const next = new Set(current || parkedSet);
      if (off) next.add(key); else next.delete(key);
      return next;
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      // Merged over what is already configured, so mapping one status never drops
      // the rest of the team's map.
      const statusPositions = { ...(data.statusPositions || {}) };
      for (const [key, value] of Object.entries(draft)) statusPositions[key] = value;

      const updates = { statusPositions };
      // Only send parkedStatuses when it actually changed, so saving a position
      // never rewrites the parked list out from under another editor.
      if (parkedDirty) updates.parkedStatuses = [...parkedSet];

      await updateShapeUpSettings(organizationId, updates);
      await load();
      if (onSaved) onSaved();
    } catch (err) {
      setError(err.response?.data?.message || 'Could not save the map.');
    } finally {
      setSaving(false);
    }
  };

  const discard = () => { setDraft({}); setParkedDraft(null); };

  if (loading && !data) return <Skeleton variant="rectangular" height={160} sx={{ borderRadius: 2 }} />;

  return (
    <Box>
      <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
        How your Jira statuses sit on the hill
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        A hill chart is about unknowns, and a Jira status is about workflow, so the two
        only line up where you say they do. Statuses left unmapped are placed by Jira&apos;s
        status category and held below the peak: getting past the top means claiming there
        are no unknowns left, which is a call for a person to make.
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      {data?.jiraError && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {`Jira could not be read (${data.jiraError}), so this only lists statuses already
            seen on a scope. Connect Jira to see every column on the board.`}
        </Alert>
      )}

      {(data?.statuses || []).length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No statuses yet. Sync a bet from a Jira epic and its statuses will appear here.
        </Typography>
      ) : (
        <>
          <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap', rowGap: 1 }}>
            <Chip size="small" label={`${data.mapped} mapped`} color="primary" variant="outlined" />
            {data.provisional > 0 && (
              <Tooltip title="These are positioned by category until somebody maps them. They cannot cross the peak.">
                <Chip
                  size="small"
                  icon={<HelpOutlineIcon sx={{ fontSize: 14 }} />}
                  label={`${data.provisional} guessed`}
                  color="warning"
                  variant="outlined"
                />
              </Tooltip>
            )}
            {data.projectKeys?.length > 0 && (
              <Chip size="small" variant="outlined" label={`Projects: ${data.projectKeys.join(', ')}`} />
            )}
          </Stack>

          {statuses.length === 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Every status is parked. Put one back below to place it on the hill.
            </Typography>
          )}

          <Stack divider={<Divider />} spacing={0}>
            {statuses.map((status) => {
              const value = draft[status.key] ?? status.position ?? 0;
              const changed = draft[status.key] !== undefined;
              return (
                <Box key={status.key} sx={{ py: 1.25 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, minWidth: 150 }}>
                      {status.name}
                    </Typography>
                    <Chip size="small" variant="outlined" label={zoneLabel(value)} />
                    {status.isProvisional && !changed && (
                      <Chip size="small" color="warning" variant="outlined" label="guessed" />
                    )}
                    {status.scopeCount > 0 && (
                      <Typography variant="caption" color="text.secondary">
                        {status.scopeCount === 1 ? '1 scope' : `${status.scopeCount} scopes`}
                      </Typography>
                    )}
                    <Box sx={{ flexGrow: 1 }} />
                    <Typography variant="caption" color="text.disabled">
                      {Math.round(value * 100)}%
                    </Typography>
                    {canManage && (
                      <Button size="small" color="inherit" onClick={() => setParked(status.key, true)}>
                        Park
                      </Button>
                    )}
                  </Box>
                  {canManage ? (
                    <Slider
                      size="small"
                      min={0}
                      max={1}
                      step={0.005}
                      value={value}
                      onChange={(_, next) => setDraft(current => ({ ...current, [status.key]: next }))}
                      // The peak is the only mark worth showing: it is the line
                      // between "still working it out" and "just execution left".
                      marks={[{ value: SUMMIT, label: 'peak' }]}
                      sx={{ mt: 0.5 }}
                      aria-label={`Hill position for ${status.name}`}
                    />
                  ) : (
                    <Box sx={{ mt: 0.5, height: 4, borderRadius: 2, bgcolor: 'action.hover', position: 'relative' }}>
                      <Box sx={{ position: 'absolute', left: `${value * 100}%`, top: -3, width: 10, height: 10, borderRadius: '50%', bgcolor: 'primary.main' }} />
                    </Box>
                  )}
                </Box>
              );
            })}
          </Stack>

          <Box sx={{ mt: 3 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              Off the hill
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Parked statuses sit off the hill entirely: their scopes leave the chart until
              the work comes back. Use this for columns like a backlog or a blocked lane that
              are not part of climbing the hill.
            </Typography>
            {parkedStatuses.length === 0 ? (
              <Typography variant="caption" color="text.disabled">
                Nothing is parked. Every status above appears on the hill.
              </Typography>
            ) : (
              <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', rowGap: 1 }}>
                {parkedStatuses.map((status) => (
                  <Chip
                    key={status.key}
                    label={status.scopeCount > 0 ? `${status.name} (${status.scopeCount})` : status.name}
                    variant="outlined"
                    onDelete={canManage ? () => setParked(status.key, false) : undefined}
                    deleteIcon={canManage ? <span style={{ fontSize: 12, padding: '0 6px' }}>put back</span> : undefined}
                  />
                ))}
              </Stack>
            )}
          </Box>

          {canManage && (
            <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
              <Button variant="contained" onClick={save} disabled={!dirty || saving}>
                {saving ? 'Saving…' : 'Save the map'}
              </Button>
              {dirty && <Button color="inherit" onClick={discard}>Discard</Button>}
            </Stack>
          )}
        </>
      )}
    </Box>
  );
};

export default HillStatusMap;
